import { useState, useEffect } from "react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import {
  CalendarDays, Clock, MapPin, Users, Ticket,
  Search, ChevronRight, ArrowLeft, Star, Share2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = apiUrl;

// ── Example fallback event (shown when API returns nothing) ───────────────────
const EXAMPLE_EVENT = {
  id: "demo-1",
  title: "Chef's Table: Festive Tasting Dinner",
  description:
    "Join us for an exclusive 7-course tasting menu curated by our head chef. " +
    "Each dish is paired with hand-selected wines and accompanied by stories from the kitchen. " +
    "Limited to 20 guests — an intimate evening you won't forget.",
  category: "Special Dinner",
  event_date: (() => {
    const d = new Date();
    d.setDate(d.getDate() + 12);
    return d.toISOString().split("T")[0];
  })(),
  event_time: "19:30",
  duration_mins: 150,
  location: "Private Dining Room, Main Branch",
  capacity: 20,
  ticket_price: 1499,
  image_url: "",
  status: "published",
  is_featured: true,
  tags: ["tasting-menu", "wine-pairing", "intimate", "chef-special"],
};

const CATEGORIES = [
  "All", "Special Dinner", "Workshop", "Live Music", "Seasonal Menu",
  "Tasting Event", "Private Dining", "Holiday Special", "Other",
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "long", year: "numeric" }) : "—";

const fmtCurrency = (v) =>
  v > 0 ? `₹${Number(v).toLocaleString("en-IN")}` : "Free";

// ── Event Card ────────────────────────────────────────────────────────────────
const EventCard = ({ event, onClick }) => {
  const isPast = new Date(event.event_date) < new Date();
  return (
    <div
      onClick={() => onClick(event)}
      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
    >
      {/* Cover */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-orange-100 to-amber-50">
        {event.image_url
          ? <img src={event.image_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center">
              <CalendarDays size={40} className="text-orange-300" />
            </div>
        }
        {/* Overlays */}
        {event.is_featured && (
          <span className="absolute top-3 left-3 bg-orange-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider shadow">
            Featured
          </span>
        )}
        {isPast && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/30">Event Ended</span>
          </div>
        )}
        <div className="absolute bottom-3 right-3">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full shadow ${event.ticket_price > 0 ? "bg-white text-gray-900" : "bg-green-500 text-white"}`}>
            {fmtCurrency(event.ticket_price)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="text-xs font-semibold text-orange-500 uppercase tracking-wider mb-1">{event.category}</p>
        <h3 className="font-extrabold text-gray-900 text-base leading-tight mb-3 group-hover:text-orange-600 transition-colors">
          {event.title}
        </h3>

        <div className="space-y-1.5 mb-4">
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <CalendarDays size={14} className="text-orange-400 flex-shrink-0" />
            {fmtDate(event.event_date)}
          </p>
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Clock size={14} className="text-orange-400 flex-shrink-0" />
            {event.event_time}{event.duration_mins ? ` · ${Math.round(event.duration_mins/60)}h${event.duration_mins%60 ? `${event.duration_mins%60}m` : ""}` : ""}
          </p>
          {event.location && (
            <p className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin size={14} className="text-orange-400 flex-shrink-0" />
              {event.location}
            </p>
          )}
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Users size={14} className="text-orange-400 flex-shrink-0" />
            {event.capacity} seats available
          </p>
        </div>

        <button className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition flex items-center justify-center gap-2">
          View Details <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

// ── Event Detail Modal ────────────────────────────────────────────────────────
const EventDetail = ({ event, onClose }) => {
  if (!event) return null;
  const isPast = new Date(event.event_date) < new Date();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Image */}
        <div className="relative h-52 flex-shrink-0 bg-gradient-to-br from-orange-100 to-amber-50">
          {event.image_url
            ? <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center"><CalendarDays size={48} className="text-orange-300" /></div>
          }
          <button onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 bg-white/80 hover:bg-white rounded-full flex items-center justify-center backdrop-blur-sm shadow transition">
            <ArrowLeft size={16} className="text-gray-700" />
          </button>
          {event.is_featured && (
            <span className="absolute top-4 left-4 bg-orange-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider">Featured</span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <p className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-1">{event.category}</p>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-4 leading-tight">{event.title}</h2>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            {[
              { icon: CalendarDays, label: "Date",     value: fmtDate(event.event_date) },
              { icon: Clock,        label: "Time",     value: `${event.event_time}${event.duration_mins ? ` (${event.duration_mins} min)` : ""}` },
              { icon: MapPin,       label: "Location", value: event.location || "To be announced" },
              { icon: Users,        label: "Capacity", value: `${event.capacity} seats` },
              { icon: Ticket,       label: "Ticket",   value: fmtCurrency(event.ticket_price) },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 font-semibold mb-0.5">{label}</p>
                <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                  <Icon size={13} className="text-orange-400 flex-shrink-0" />{value}
                </p>
              </div>
            ))}
          </div>

          {/* Description */}
          {event.description && (
            <div className="mb-5">
              <h4 className="text-sm font-bold text-gray-700 mb-2">About This Event</h4>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {(Array.isArray(event.tags) ? event.tags : event.tags.split(",")).map((tag, i) => (
                <span key={i} className="text-xs font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">{tag.trim()}</span>
              ))}
            </div>
          )}

          {/* CTA */}
          {!isPast && (
            <button
              onClick={() => { alert(`Booking for "${event.title}" — integrate with your reservations flow!`); }}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-extrabold rounded-2xl transition text-base flex items-center justify-center gap-2 shadow-lg shadow-orange-200"
            >
              <Ticket size={18} />
              {event.ticket_price > 0 ? `Book Now · ${fmtCurrency(event.ticket_price)}/person` : "Reserve Your Spot (Free)"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const EventsPage = () => {
  const [events,    setEvents]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [category,  setCategory]  = useState("All");
  const [showPast,  setShowPast]  = useState(false);
  const [selected,  setSelected]  = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/events?license_id=2&status=published`);
        const list = res.data?.list || [];
        setEvents(list.length > 0 ? list : [EXAMPLE_EVENT]);
      } catch {
        setEvents([EXAMPLE_EVENT]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const today = new Date();

  const filtered = events.filter(ev => {
    const isPast       = new Date(ev.event_date) < today;
    if (!showPast && isPast) return false;
    const matchCat     = category === "All" || ev.category === category;
    const q = search.toLowerCase();
    const matchSearch  = !q || (ev.title || "").toLowerCase().includes(q)
      || (ev.location || "").toLowerCase().includes(q)
      || (ev.description || "").toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const featured  = filtered.filter(ev => ev.is_featured);
  const regular   = filtered.filter(ev => !ev.is_featured);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-400 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest uppercase opacity-80 mb-3">What's On</p>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">Events & Experiences</h1>
          <p className="text-lg opacity-90 mb-8">Special dinners, workshops, seasonal menus — and more unforgettable nights.</p>

          {/* Search */}
          <div className="relative max-w-lg mx-auto">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl text-gray-900 text-sm shadow-lg focus:outline-none focus:ring-2 focus:ring-white/60"
              placeholder="Search events, locations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Category filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
          {CATEGORIES.map(c => (
            <button key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition flex-shrink-0
                ${category === c ? "bg-orange-500 text-white border-orange-500 shadow-md" : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}>
              {c}
            </button>
          ))}
          <button
            onClick={() => setShowPast(!showPast)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition flex-shrink-0
              ${showPast ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>
            {showPast ? "Hide Past" : "Show Past Events"}
          </button>
        </div>

        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl h-80 animate-pulse">
                <div className="h-48 bg-gray-100 rounded-t-2xl" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="py-20 text-center text-gray-400">
            <CalendarDays size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-bold text-lg text-gray-600">No events found</p>
            <p className="text-sm mt-1">Check back soon for upcoming events!</p>
          </div>
        )}

        {/* Featured */}
        {featured.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-extrabold text-gray-900 mb-4 flex items-center gap-2">
              <Star size={18} className="text-amber-400 fill-amber-400" /> Featured Events
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {featured.map(ev => <EventCard key={ev.id} event={ev} onClick={setSelected} />)}
            </div>
          </div>
        )}

        {/* All events */}
        {regular.length > 0 && (
          <div>
            {featured.length > 0 && <h2 className="text-xl font-extrabold text-gray-900 mb-4">All Events</h2>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {regular.map(ev => <EventCard key={ev.id} event={ev} onClick={setSelected} />)}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && <EventDetail event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default EventsPage;
